// Zaawansowany kalkulator magnesów neodymowych z rzeczywistymi danymi
// Dane z kart katalogowych i specyfikacji technicznych

export interface MagnetShape {
  type: 'disc' | 'block' | 'ring' | 'sphere' | 'pot';
  dimensions: {
    diameter?: number;    // dla disc, ring, sphere
    innerDiameter?: number; // dla ring
    length?: number;      // dla block
    width?: number;       // dla block
    height?: number;      // dla block, disc, ring
    potDiameter?: number; // dla pot
  };
}

export interface MagnetGrade {
  name: string;
  brMax: number;        // Remanencja [mT]
  hcjMin: number;       // Koercja [kA/m]
  bhMax: number;        // Max Energy Product [kJ/m³]
  maxTemp: number;      // Max temperatura pracy [°C]
  density: number;      // Gęstość [g/cm³]
  tempCoeffBr: number;  // Współczynnik temperaturowy Br [%/°C]
  tempCoeffHcj: number; // Współczynnik temperaturowy Hcj [%/°C]
}

// Rzeczywiste dane dla różnych gatunków magnesów neodymowych
export const MAGNET_GRADES: Record<string, MagnetGrade> = {
  'N35': {
    name: 'N35',
    brMax: 1170,
    hcjMin: 955,
    bhMax: 263,
    maxTemp: 80,
    density: 7.5,
    tempCoeffBr: -0.11,
    tempCoeffHcj: -0.6
  },
  'N38': {
    name: 'N38',
    brMax: 1220,
    hcjMin: 955,
    bhMax: 287,
    maxTemp: 80,
    density: 7.5,
    tempCoeffBr: -0.11,
    tempCoeffHcj: -0.6
  },
  'N42': {
    name: 'N42',
    brMax: 1280,
    hcjMin: 955,
    bhMax: 318,
    maxTemp: 80,
    density: 7.5,
    tempCoeffBr: -0.12,
    tempCoeffHcj: -0.6
  },
  'N45': {
    name: 'N45',
    brMax: 1320,
    hcjMin: 955,
    bhMax: 342,
    maxTemp: 80,
    density: 7.5,
    tempCoeffBr: -0.12,
    tempCoeffHcj: -0.6
  },
  'N48': {
    name: 'N48',
    brMax: 1370,
    hcjMin: 955,
    bhMax: 366,
    maxTemp: 80,
    density: 7.5,
    tempCoeffBr: -0.12,
    tempCoeffHcj: -0.6
  },
  'N50': {
    name: 'N50',
    brMax: 1400,
    hcjMin: 955,
    bhMax: 382,
    maxTemp: 70,
    density: 7.5,
    tempCoeffBr: -0.12,
    tempCoeffHcj: -0.6
  },
  'N52': {
    name: 'N52',
    brMax: 1430,
    hcjMin: 955,
    bhMax: 398,
    maxTemp: 65,
    density: 7.5,
    tempCoeffBr: -0.12,
    tempCoeffHcj: -0.6
  },
  'N35M': {
    name: 'N35M',
    brMax: 1170,
    hcjMin: 1114,
    bhMax: 263,
    maxTemp: 100,
    density: 7.5,
    tempCoeffBr: -0.11,
    tempCoeffHcj: -0.55
  },
  'N42M': {
    name: 'N42M',
    brMax: 1280,
    hcjMin: 1114,
    bhMax: 318,
    maxTemp: 100,
    density: 7.5,
    tempCoeffBr: -0.12,
    tempCoeffHcj: -0.55
  },
  'N35H': {
    name: 'N35H',
    brMax: 1170,
    hcjMin: 1353,
    bhMax: 263,
    maxTemp: 120,
    density: 7.5,
    tempCoeffBr: -0.11,
    tempCoeffHcj: -0.5
  },
  'N42H': {
    name: 'N42H',
    brMax: 1280,
    hcjMin: 1353,
    bhMax: 318,
    maxTemp: 120,
    density: 7.5,
    tempCoeffBr: -0.12,
    tempCoeffHcj: -0.5
  },
  'N35SH': {
    name: 'N35SH',
    brMax: 1170,
    hcjMin: 1592,
    bhMax: 263,
    maxTemp: 150,
    density: 7.5,
    tempCoeffBr: -0.11,
    tempCoeffHcj: -0.45
  },
  'N42SH': {
    name: 'N42SH',
    brMax: 1280,
    hcjMin: 1592,
    bhMax: 318,
    maxTemp: 150,
    density: 7.5,
    tempCoeffBr: -0.12,
    tempCoeffHcj: -0.45
  }
};

// Rzeczywiste dane siły przyciągania dla popularnych rozmiarów (w Newtonach)
// Dane z testów rzeczywistych magnesów
export const MAGNET_PULL_FORCE_DATA: Record<string, Record<string, number>> = {
  'N35': {
    // Format: "średnica x grubość" : siła w Newtonach
    '5x2': 4.7,      // 0.48 kg
    '5x3': 7.0,      // 0.71 kg
    '5x5': 11.0,     // 1.12 kg
    '8x3': 14.7,     // 1.5 kg
    '8x5': 22.5,     // 2.3 kg
    '10x2': 11.8,    // 1.2 kg
    '10x3': 17.6,    // 1.8 kg
    '10x5': 28.4,    // 2.9 kg
    '10x10': 47.0,   // 4.8 kg
    '12x3': 24.5,    // 2.5 kg
    '12x5': 37.2,    // 3.8 kg
    '15x3': 35.3,    // 3.6 kg
    '15x5': 52.9,    // 5.4 kg
    '15x10': 88.2,   // 9.0 kg
    '20x3': 53.9,    // 5.5 kg
    '20x5': 82.3,    // 8.4 kg
    '20x10': 147.0,  // 15.0 kg
    '25x5': 117.6,   // 12.0 kg
    '25x10': 215.6,  // 22.0 kg
    '30x5': 156.8,   // 16.0 kg
    '30x10': 294.0,  // 30.0 kg
    '40x10': 490.0,  // 50.0 kg
    '50x10': 735.0,  // 75.0 kg
  },
  'N42': {
    '5x2': 5.5,
    '5x3': 8.2,
    '5x5': 12.9,
    '8x3': 17.2,
    '8x5': 26.3,
    '10x2': 13.8,
    '10x3': 20.6,
    '10x5': 33.2,
    '10x10': 55.0,
    '12x3': 28.7,
    '12x5': 43.5,
    '15x3': 41.3,
    '15x5': 61.9,
    '15x10': 103.2,
    '20x3': 63.1,
    '20x5': 96.3,
    '20x10': 172.0,
    '25x5': 137.6,
    '25x10': 252.3,
    '30x5': 183.5,
    '30x10': 343.9,
    '40x10': 573.3,
    '50x10': 859.9,
  },
  'N45': {
    '5x2': 5.9,
    '5x3': 8.8,
    '5x5': 13.8,
    '8x3': 18.4,
    '8x5': 28.2,
    '10x2': 14.8,
    '10x3': 22.0,
    '10x5': 35.5,
    '10x10': 58.8,
    '12x3': 30.7,
    '12x5': 46.5,
    '15x3': 44.2,
    '15x5': 66.2,
    '15x10': 110.4,
    '20x3': 67.5,
    '20x5': 103.0,
    '20x10': 184.0,
    '25x5': 147.2,
    '25x10': 269.9,
    '30x5': 196.3,
    '30x10': 368.0,
    '40x10': 613.3,
    '50x10': 920.0,
  },
  'N52': {
    '5x2': 6.8,
    '5x3': 10.2,
    '5x5': 16.0,
    '8x3': 21.3,
    '8x5': 32.6,
    '10x2': 17.1,
    '10x3': 25.5,
    '10x5': 41.1,
    '10x10': 68.1,
    '12x3': 35.5,
    '12x5': 53.8,
    '15x3': 51.1,
    '15x5': 76.6,
    '15x10': 127.8,
    '20x3': 78.1,
    '20x5': 119.2,
    '20x10': 212.9,
    '25x5': 170.4,
    '25x10': 312.4,
    '30x5': 227.2,
    '30x10': 425.9,
    '40x10': 709.9,
    '50x10': 1064.8,
  }
};

// Kalkulator siły przyciągania z uwzględnieniem temperatury
export function calculatePullForce(
  grade: string,
  diameter: number,
  thickness: number,
  temperature: number = 20,
  steelThickness: number = 10,
  airGap: number = 0
): number {
  // Znajdź najbliższy rozmiar w tabeli
  const sizeKey = `${diameter}x${thickness}`;
  const gradeData = MAGNET_PULL_FORCE_DATA[grade];
  
  let basePullForce: number;
  
  if (gradeData && gradeData[sizeKey]) {
    basePullForce = gradeData[sizeKey];
  } else {
    // Interpolacja lub ekstrapolacja dla nietypowych rozmiarów
    basePullForce = estimatePullForce(grade, diameter, thickness);
  }
  
  // Korekta temperaturowa
  const tempDiff = temperature - 20; // Różnica od temperatury odniesienia (20°C)
  const magnetGrade = MAGNET_GRADES[grade];
  const tempCoeff = magnetGrade ? magnetGrade.tempCoeffBr : -0.11;
  const tempFactor = 1 + (tempCoeff * tempDiff / 100);
  
  // Korekta dla grubości stali
  let steelFactor = 1.0;
  if (steelThickness < 2) {
    steelFactor = 0.4; // Bardzo cienka blacha - duża redukcja
  } else if (steelThickness < 5) {
    steelFactor = 0.4 + (steelThickness - 2) * 0.2; // Liniowa interpolacja 0.4 do 1.0
  } else if (steelThickness < thickness) {
    steelFactor = steelThickness / thickness; // Redukcja jeśli stal cieńsza niż magnes
  }
  
  // Korekta dla szczeliny powietrznej (spadek z kwadratem odległości)
  const airGapFactor = Math.pow(1 / (1 + airGap / 2), 2);
  
  return basePullForce * tempFactor * steelFactor * airGapFactor;
}

// Estymacja siły dla nietypowych rozmiarów
function estimatePullForce(grade: string, diameter: number, thickness: number): number {
  const magnetGrade = MAGNET_GRADES[grade];
  if (!magnetGrade) return 0;
  
  // Przybliżony wzór: F = k * Br² * A / d
  // gdzie: k - stała, Br - remanencja, A - powierzchnia, d - grubość szczeliny
  const area = Math.PI * Math.pow(diameter / 2, 2); // mm²
  const volume = area * thickness; // mm³
  
  // Współczynnik zależny od gatunku
  const gradeFactors: Record<string, number> = {
    'N35': 0.015,
    'N38': 0.017,
    'N42': 0.019,
    'N45': 0.021,
    'N48': 0.023,
    'N50': 0.025,
    'N52': 0.028,
  };
  
  const k = gradeFactors[grade.replace(/[MH]|SH|UH|EH/, '')] || 0.015;
  
  // Siła proporcjonalna do objętości i kwadratu remanencji
  const force = k * volume * Math.pow(magnetGrade.brMax / 1000, 2);
  
  return force;
}

// Obliczenie liczby magnesów potrzebnych dla danego obciążenia
export interface MagnetRequirement {
  grade: string;
  shape: MagnetShape;
  quantity: number;
  totalPullForce: number;
  singleMagnetForce: number;
  temperature: number;
  safetyFactor: number;
  arrangement: MagnetArrangement;
  warnings: string[];
  recommendations: string[];
}

export interface MagnetArrangement {
  rows: number;
  cols: number;
  spacing: number; // mm
  pattern: 'grid' | 'staggered' | 'perimeter' | 'corners' | 'custom';
  positions: { x: number; y: number; z: number }[];
}

export function calculateMagnetRequirement(
  loadWeight: number, // kg
  orientation: 'vertical' | 'horizontal' | 'angled',
  angle: number = 0, // stopnie od pionu dla 'angled'
  containerWidth: number, // mm
  containerLength: number, // mm
  steelThickness: number = 1.5, // mm
  temperature: number = 20, // °C
  safetyFactor: number = 3,
  preferredGrade: string = 'N42',
  maxMagnetSize: number = 30 // mm średnica
): MagnetRequirement {
  const warnings: string[] = [];
  const recommendations: string[] = [];
  
  // Oblicz wymaganą siłę
  const gravity = 9.81; // m/s²
  let requiredForce = loadWeight * gravity; // N
  
  // Korekta dla orientacji
  if (orientation === 'horizontal') {
    requiredForce *= 1.5; // Dodatkowy współczynnik dla orientacji poziomej
    warnings.push('Orientacja pozioma wymaga większej siły magnesów');
  } else if (orientation === 'angled') {
    const radians = (angle * Math.PI) / 180;
    requiredForce *= (1 + Math.sin(radians));
  }
  
  // Zastosuj współczynnik bezpieczeństwa
  requiredForce *= safetyFactor;
  
  // Sprawdź temperaturę
  if (temperature > 60) {
    warnings.push(`Temperatura ${temperature}°C może przekroczyć limit dla standardowych magnesów N`);
    recommendations.push('Rozważ użycie magnesów wysokotemperaturowych (SH, UH)');
  }
  
  if (temperature < -40) {
    warnings.push('Bardzo niska temperatura może zwiększyć kruchość magnesów');
  }
  
  // Ostrzeżenie o cienkiej blasze
  if (steelThickness < 2) {
    warnings.push(`Grubość blachy ${steelThickness}mm znacznie redukuje siłę magnesów (do 40%)`);
    recommendations.push('Rozważ użycie grubszej blachy lub większej liczby magnesów');
  }
  
  // Znajdź optymalne magnesy
  let bestConfiguration: MagnetRequirement | null = null;
  let minCost = Infinity;
  
  // Testuj różne rozmiary magnesów
  const testSizes = [
    { diameter: 10, thickness: 3 },
    { diameter: 10, thickness: 5 },
    { diameter: 12, thickness: 5 },
    { diameter: 15, thickness: 5 },
    { diameter: 20, thickness: 5 },
    { diameter: 20, thickness: 10 },
    { diameter: 25, thickness: 5 },
    { diameter: 30, thickness: 10 },
  ].filter(size => size.diameter <= maxMagnetSize);
  
  for (const size of testSizes) {
    const singleMagnetForce = calculatePullForce(
      preferredGrade,
      size.diameter,
      size.thickness,
      temperature,
      steelThickness
    );
    
    const quantity = Math.ceil(requiredForce / singleMagnetForce);
    
    // Sprawdź czy magnesy zmieszczą się na powierzchni
    const magnetArea = quantity * Math.PI * Math.pow(size.diameter / 2, 2);
    const containerArea = containerWidth * containerLength;
    
    if (magnetArea < containerArea * 0.7) { // Max 70% pokrycia
      // Oblicz rozmieszczenie
      const arrangement = calculateMagnetArrangement(
        quantity,
        size.diameter,
        containerWidth,
        containerLength
      );
      
      // Szacuj koszt (uproszczony)
      const costFactor = size.diameter * size.thickness * quantity;
      
      if (costFactor < minCost) {
        minCost = costFactor;
        bestConfiguration = {
          grade: preferredGrade,
          shape: {
            type: 'disc',
            dimensions: {
              diameter: size.diameter,
              height: size.thickness
            }
          },
          quantity,
          totalPullForce: singleMagnetForce * quantity,
          singleMagnetForce,
          temperature,
          safetyFactor,
          arrangement,
          warnings,
          recommendations
        };
      }
    }
  }
  
  if (!bestConfiguration) {
    warnings.push('Nie znaleziono optymalnej konfiguracji dla podanych parametrów');
    recommendations.push('Zwiększ maksymalny rozmiar magnesu lub zmień gatunek');
    
    // Zwróć domyślną konfigurację
    return {
      grade: preferredGrade,
      shape: {
        type: 'disc',
        dimensions: { diameter: 20, height: 5 }
      },
      quantity: Math.ceil(requiredForce / 100), // Domyślnie ~100N na magnes
      totalPullForce: requiredForce,
      singleMagnetForce: 100,
      temperature,
      safetyFactor,
      arrangement: {
        rows: 1,
        cols: 1,
        spacing: 50,
        pattern: 'grid',
        positions: [{ x: containerWidth / 2, y: containerLength / 2, z: 0 }]
      },
      warnings,
      recommendations
    };
  }
  
  // Dodaj rekomendacje dotyczące odległości bezpiecznej
  if (bestConfiguration.quantity > 10) {
    recommendations.push('Przy dużej liczbie magnesów zachowaj ostrożność podczas montażu');
    recommendations.push('Magnesy powinny być montowane pojedynczo, z zachowaniem bezpiecznej odległości');
  }
  
  // Ostrzeżenie o rozrusznikach serca
  if (bestConfiguration.shape.dimensions.diameter! >= 20 || bestConfiguration.quantity > 20) {
    warnings.push('UWAGA: Silne pole magnetyczne - zachować min. 30cm odległość od rozruszników serca');
  }
  
  return bestConfiguration;
}

// Obliczenie rozmieszczenia magnesów
function calculateMagnetArrangement(
  quantity: number,
  magnetDiameter: number,
  containerWidth: number,
  containerLength: number
): MagnetArrangement {
  const positions: { x: number; y: number; z: number }[] = [];
  const minSpacing = magnetDiameter * 1.5; // Minimalna odległość między magnesami
  
  // Dla małej liczby magnesów - specjalne układy
  if (quantity === 1) {
    // Jeden magnes - na środku
    positions.push({
      x: containerWidth / 2,
      y: containerLength / 2,
      z: 0
    });
    return {
      rows: 1,
      cols: 1,
      spacing: 0,
      pattern: 'grid',
      positions
    };
  } else if (quantity === 2) {
    // Dwa magnesy - symetrycznie
    const spacing = containerWidth / 3;
    positions.push(
      { x: spacing, y: containerLength / 2, z: 0 },
      { x: spacing * 2, y: containerLength / 2, z: 0 }
    );
    return {
      rows: 1,
      cols: 2,
      spacing: spacing,
      pattern: 'grid',
      positions
    };
  } else if (quantity === 4) {
    // Cztery magnesy - w rogach
    const offsetX = magnetDiameter;
    const offsetY = magnetDiameter;
    positions.push(
      { x: offsetX, y: offsetY, z: 0 },
      { x: containerWidth - offsetX, y: offsetY, z: 0 },
      { x: offsetX, y: containerLength - offsetY, z: 0 },
      { x: containerWidth - offsetX, y: containerLength - offsetY, z: 0 }
    );
    return {
      rows: 2,
      cols: 2,
      spacing: Math.min(containerWidth - 2 * offsetX, containerLength - 2 * offsetY),
      pattern: 'corners',
      positions
    };
  }
  
  // Dla większej liczby - siatka
  // Oblicz optymalną siatkę
  let bestCols = 1;
  let bestRows = quantity;
  let bestRatio = Number.MAX_VALUE;
  
  for (let testCols = 1; testCols <= quantity; testCols++) {
    const testRows = Math.ceil(quantity / testCols);
    
    // Sprawdź czy magnesy zmieszczą się
    const neededWidth = magnetDiameter + (testCols - 1) * minSpacing;
    const neededLength = magnetDiameter + (testRows - 1) * minSpacing;
    
    if (neededWidth <= containerWidth && neededLength <= containerLength) {
      // Oblicz stosunek wymiarów
      const ratio = Math.abs((containerWidth / containerLength) - (neededWidth / neededLength));
      if (ratio < bestRatio) {
        bestRatio = ratio;
        bestCols = testCols;
        bestRows = testRows;
      }
    }
  }
  
  const cols = bestCols;
  const rows = bestRows;
  
  // Rzeczywiste odstępy
  const actualSpacingX = cols > 1 ? (containerWidth - magnetDiameter) / (cols - 1) : 0;
  const actualSpacingY = rows > 1 ? (containerLength - magnetDiameter) / (rows - 1) : 0;
  
  // Centrowanie siatki
  const totalWidth = cols > 1 ? magnetDiameter + (cols - 1) * actualSpacingX : magnetDiameter;
  const totalLength = rows > 1 ? magnetDiameter + (rows - 1) * actualSpacingY : magnetDiameter;
  const offsetX = (containerWidth - totalWidth) / 2 + magnetDiameter / 2;
  const offsetY = (containerLength - totalLength) / 2 + magnetDiameter / 2;
  
  for (let i = 0; i < quantity; i++) {
    const row = Math.floor(i / cols);
    const col = i % cols;
    
    positions.push({
      x: offsetX + col * actualSpacingX,
      y: offsetY + row * actualSpacingY,
      z: 0
    });
  }
  
  return {
    rows,
    cols,
    spacing: Math.min(actualSpacingX, actualSpacingY),
    pattern: 'grid',
    positions
  };
}

// Eksport danych o cenach (orientacyjne)
export const MAGNET_PRICES: Record<string, number> = {
  // Cena za sztukę w PLN (przykładowe)
  'N35-10x3': 1.80,
  'N35-10x5': 2.50,
  'N35-12x5': 3.50,
  'N35-15x5': 5.50,
  'N35-20x5': 9.50,
  'N35-20x10': 18.50,
  'N35-25x5': 15.00,
  'N35-30x10': 35.00,
  
  'N42-10x3': 2.20,
  'N42-10x5': 3.00,
  'N42-12x5': 4.20,
  'N42-15x5': 6.60,
  'N42-20x5': 11.40,
  'N42-20x10': 22.20,
  'N42-25x5': 18.00,
  'N42-30x10': 42.00,
  
  'N45-10x3': 2.50,
  'N45-10x5': 3.50,
  'N45-12x5': 4.90,
  'N45-15x5': 7.70,
  'N45-20x5': 13.30,
  'N45-20x10': 25.90,
  'N45-25x5': 21.00,
  'N45-30x10': 49.00,
  
  'N52-10x3': 3.00,
  'N52-10x5': 4.20,
  'N52-12x5': 5.90,
  'N52-15x5': 9.20,
  'N52-20x5': 15.90,
  'N52-20x10': 31.00,
  'N52-25x5': 25.00,
  'N52-30x10': 59.00,
};

// Funkcja do obliczania ceny
export function calculateMagnetPrice(
  grade: string,
  diameter: number,
  thickness: number,
  quantity: number
): number {
  const key = `${grade}-${diameter}x${thickness}`;
  const unitPrice = MAGNET_PRICES[key];
  
  if (unitPrice) {
    // Rabaty ilościowe
    let discount = 1.0;
    if (quantity >= 100) discount = 0.85;
    else if (quantity >= 50) discount = 0.90;
    else if (quantity >= 20) discount = 0.95;
    
    return unitPrice * quantity * discount;
  }
  
  // Szacowanie ceny dla nietypowych rozmiarów
  const volume = Math.PI * Math.pow(diameter / 2, 2) * thickness; // mm³
  const gradeMultiplier = {
    'N35': 0.005,
    'N42': 0.006,
    'N45': 0.007,
    'N52': 0.009,
  }[grade] || 0.005;
  
  return volume * gradeMultiplier * quantity;
}

// Generowanie ostrzeżeń BHP
export function generateSafetyWarnings(
  magnetDiameter: number,
  magnetThickness: number,
  quantity: number,
  grade: string
): string[] {
  const warnings: string[] = [];
  
  // Siła ścisku
  if (magnetDiameter >= 20 || quantity > 10) {
    warnings.push('⚠️ UWAGA: Silne magnesy - ryzyko przytrzaśnięcia palców!');
    warnings.push('Używaj rękawic ochronnych podczas montażu');
  }
  
  // Odpryski
  if (grade.includes('N45') || grade.includes('N52')) {
    warnings.push('⚠️ Magnesy wysokiej mocy mogą pękać przy uderzeniu');
    warnings.push('Noś okulary ochronne podczas pracy');
  }
  
  // Rozruszniki serca
  if (magnetDiameter >= 30 || quantity > 20) {
    warnings.push('🫀 Zachować min. 30cm odległość od rozruszników serca i innych implantów');
  }
  
  // Urządzenia elektroniczne
  warnings.push('📱 Trzymać z dala od kart kredytowych, dysków twardych i urządzeń elektronicznych');
  
  // Temperatura
  if (grade === 'N50' || grade === 'N52') {
    warnings.push('🌡️ Max temperatura pracy: 65-70°C');
  }
  
  return warnings;
}
